import { EntityViewDisplay, type EntityViewKanban } from '@jms/model';
import type { GridApi } from 'ag-grid-enterprise';
import type { EntityBoardPanelAfterSave } from '../../context';
import type { BoardPanelRefreshScope } from './board.panel.host';

/**
 * What a `BoardPanelHost.refresh(scope)` has to reach.
 *
 * A scope decision, not a side effect: the board owns the queries and the grid, so this only says
 * which of the three refreshes to run and the component performs them. Extracted because the
 * SSRM purge is easy to lose in a component method and a lost purge is invisible — the deleted
 * outline row simply stays on screen.
 */
export interface BoardPanelRefreshPlan {
  /** Invalidate `[entity, 'item', id]` so the focused record's own query refetches. */
  invalidateRecord: boolean;
  /** Reload the board's items query and its segment counts. */
  refreshItems: boolean;
  /**
   * Purge the server-side datasource. A projection board (the Service Plan outline tree) owns its
   * own SSRM datasource instead of reading the items query, so `refreshItems` alone leaves the
   * deleted row rendered.
   */
  purgeServerSide: boolean;
}

/**
 * Displays that render the page straight from `store.$items()` and can therefore absorb one
 * confirmed record in place.
 *
 * Table is deliberately absent: it renders through ag-grid, whose row model owns the rows, and a
 * server-side table does not read `$items()` at all. Calendar is absent for the same reason — it
 * projects records onto a time axis it recomputes from the query.
 */
const COLLECTION_DISPLAYS: readonly EntityViewDisplay[] = [EntityViewDisplay.Kanban, EntityViewDisplay.Card, EntityViewDisplay.List];

/**
 * Whether a saved record can replace its card in place instead of the board refetching the page.
 *
 * Requires the record itself — an id alone cannot be laid over the page, and guessing the rest of
 * the row from a partial payload would show state the server never confirmed.
 */
export function boardPanelUpsertsInPlace(display: EntityViewDisplay | null | undefined, record: unknown, isBulk = false): boolean {

  if (isBulk || !record || typeof record !== 'object') return false;

  return !!display && COLLECTION_DISPLAYS.includes(display);
}

/**
 * `record` touches only the focused record. `collection` additionally reloads the items query,
 * the segment counts (board state rather than a TanStack query, so nothing else refreshes them)
 * and any server-side datasource.
 *
 * `upsertsInPlace` is the collection-display shortcut: the saved record is already in hand, so the
 * page it belongs to does not have to be fetched again to show it. The record's own query is still
 * invalidated — the panel beside the board reads that one, not the page.
 *
 * `hasServerSideGrid` gates the purge on the grid's row model, not on a grid existing: a client-side
 * table has no Server-Side Row Model module, and `refreshServerSide` on it only logs AG Grid error #200.
 */
export function boardPanelRefreshPlan(scope: BoardPanelRefreshScope, hasFocusedRecord: boolean, hasServerSideGrid: boolean, upsertsInPlace = false): BoardPanelRefreshPlan {

  const collection = scope === 'collection';
  return {
    invalidateRecord: hasFocusedRecord,
    refreshItems: collection && !upsertsInPlace,
    purgeServerSide: collection && hasServerSideGrid,
  };
}

/**
 * Whether the board's grid is a live Server-Side Row Model table — the only grid a collection refresh may purge.
 * The api outlives a table swapped for another display, and a destroyed grid warns on every call.
 */
export function isLiveServerSideGrid(api: GridApi | null | undefined): boolean {

  return !!api && !api.isDestroyed() && api.getGridOption('rowModelType') === 'serverSide';
}

/**
 * What a save does to the board.
 *
 * - `replace` lays the saved record over the page. The card re-renders where it is.
 * - `regroup` is the SAME write: placements recompute off the new record and the card moves column
 *   on its own. Named separately because it is a different claim — "the card moves" — and a reader
 *   reaching for a column change should find something that says so, not a bare `upsertItem`.
 * - `refresh` refetches the page, which is the only correct answer when the record may no longer
 *   belong to the page at all.
 */
export type BoardPanelSaveAction = 'replace' | 'regroup' | 'refresh';

/**
 * Which axis, if any, a save moved the record along.
 *
 * A column change is free: every lane holds the whole column set, so the card just lands in a
 * different cell of the same page. A ROW change is not — swimlanes are paged by their row value, so
 * the record may now belong to a lane on another server page, and only a refetch can say.
 */
export function resolveKanbanAxisChange(kanban: EntityViewKanban | null | undefined, before: unknown, after: unknown): 'none' | 'column' | 'row' {

  if (!before || !after) return 'none';

  const rowPath = axisPath(kanban?.row);
  if (rowPath && !sameAxisValue(readPath(before, rowPath), readPath(after, rowPath))) return 'row';

  const columnPath = axisPath(kanban?.column);
  if (columnPath && !sameAxisValue(readPath(before, columnPath), readPath(after, columnPath))) return 'column';

  return 'none';
}

/**
 * What a panel save should do to the board, from the display, the declared mode and what changed.
 *
 * `auto` is the only mode that looks at the record: an explicit mode is a board saying it knows
 * better than the generic rule, and second-guessing it would make the setting meaningless.
 *
 * A TABLE always refreshes whatever the mode says — except an explicit `refresh`, which agrees. Its
 * rows belong to ag-grid's row model and a server-side table never reads `$items()` at all, so an
 * overlay would be written into a page nothing renders from.
 */
export function boardPanelSaveAction(display: EntityViewDisplay | null | undefined, mode: EntityBoardPanelAfterSave | undefined, axisChange: 'none' | 'column' | 'row'): BoardPanelSaveAction {

  if (!display || !COLLECTION_DISPLAYS.includes(display)) return 'refresh';
  if (mode && mode !== 'auto') return mode;

  // Only a kanban has axes. Card and List re-render the record where it is.
  if (display !== EntityViewDisplay.Kanban) return 'replace';

  switch (axisChange) {
    case 'row': return 'refresh';
    case 'column': return 'regroup';
    default: return 'replace';
  }
}

/** A kanban axis is stored as a path, but `none` is the sentinel for "no axis", not a field named none. */
function axisPath(axis: string | null | undefined): string | null {

  return axis && axis !== 'none' ? axis : null;
}

/**
 * Axis values compare on IDENTITY, not on the whole object.
 *
 * A status arrives as `{ _id, name, sortOrder, … }` and a save can legitimately return a fuller copy
 * of the same status; comparing the objects would call that a move and refetch the page for nothing.
 */
function sameAxisValue(before: unknown, after: unknown): boolean {

  return axisKey(before) === axisKey(after);
}

function axisKey(value: unknown): string {

  if (value == null) return '';
  if (typeof value !== 'object') return String(value);

  const record = value as { _id?: unknown; id?: unknown; systemName?: unknown; name?: unknown };
  return String(record._id ?? record.id ?? record.systemName ?? record.name ?? '');
}

function readPath(record: unknown, path: string): unknown {

  return path.split('.').reduce<any>((value, segment) => value?.[segment], record);
}
